<template>
  <div class="supplier-payments">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Payment</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control"
                   placeholder="Search voucher, supplier or narration" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-cash"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Paid to Suppliers</div>
          <div class="rp-card-value">{{ formatMoney(summary.amount) }}</div>
          <div class="rp-card-sub">{{ plural(summary.payment_count, 'payment') }}</div>
        </div>
      </div>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-users"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Suppliers Paid</div>
          <div class="rp-card-value">{{ formatInt(summary.supplier_count) }}</div>
          <div class="rp-card-sub">avg payment {{ formatMoney(summary.avg_payment) }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-arrow-big-up-lines"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Largest Payment</div>
          <div class="rp-card-value">{{ summary.largest ? formatMoney(summary.largest.amount) : '—' }}</div>
          <div class="rp-card-sub text-truncate" :title="summary.largest?.supplier">
            <template v-if="summary.largest">{{ summary.largest.supplier }} · {{ formatDate(summary.largest.date) }}</template>
            <template v-else>No payments in this period</template>
          </div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-building-bank"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Main Account</div>
          <div class="rp-card-value text-truncate" :title="mainAccount?.label">{{ mainAccount ? mainAccount.label : '—' }}</div>
          <div class="rp-card-sub">
            <template v-if="mainAccount">{{ formatMoney(mainAccount.amount) }} · {{ mainAccount.share }}% of payments</template>
            <template v-else>No payments in this period</template>
          </div>
        </div>
      </div>
    </div>

    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Payments {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: PURCHASE_COLOR }"></i>Amount</span>
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Payments</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No payments in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Paid from</div>
        <div v-if="accountBars.length" class="rp-bars">
          <div v-for="account in accountBars" :key="account.label">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">
                <i class="ti me-1" :class="KIND_ICONS[account.kind] || 'ti-wallet'"></i>{{ account.label }}
              </span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(account.amount) }} <span class="text-muted fw-normal">({{ account.share }}%)</span>
              </span>
            </div>
            <div class="rp-bar-track">
              <div class="rp-bar-fill" :class="KIND_TONES[account.kind]" :style="{ width: `${account.percent}%` }"></div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No payments in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Top suppliers paid</div>
        <ul v-if="summary.top_suppliers.length" class="rp-ranked">
          <li v-for="(supplier, index) in summary.top_suppliers" :key="supplier.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="supplier.name">{{ supplier.name }}</div>
              <small class="text-muted">
                {{ plural(supplier.count, 'payment') }} · last {{ formatDate(supplier.last_date) }}
              </small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(supplier.amount) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No payments in this period.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in kindPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.kind || 'all') === pill.key }" @click="setKind(pill.key)">
            <i v-if="pill.icon" class="ti" :class="pill.icon"></i>
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key">
              <td class="text-nowrap">
                {{ formatDate(row.date) }}<span v-if="row.time" class="rp-sub">{{ row.time }}</span>
              </td>
              <td>
                <div class="fw-semibold text-nowrap">{{ row.voucher_id }}</div>
                <span class="rp-sub">{{ row.voucher_type }}</span>
              </td>
              <td>
                <div class="fw-medium">{{ row.supplier }}</div>
                <span v-if="row.supplier_phone" class="rp-sub">{{ row.supplier_phone }}</span>
              </td>
              <td>
                <div v-for="source in row.sources" :key="source.label" class="text-nowrap">
                  <span class="rp-badge" :class="KIND_TONES[source.kind] || 'muted'">
                    <i class="ti" :class="KIND_ICONS[source.kind] || 'ti-wallet'"></i>{{ source.label }}
                  </span>
                  <small v-if="row.sources.length > 1" class="text-muted ms-1">{{ formatMoney(source.amount) }}</small>
                </div>
              </td>
              <td class="rp-narration" :title="row.narration">{{ row.narration || '—' }}</td>
              <td>{{ row.created_by || '—' }}</td>
              <td class="text-end fw-semibold">{{ formatMoney(row.amount) }}</td>
              <td class="text-end">
                <span :class="balanceClass(row.balance_after)">{{ formatMoney(Math.abs(Number(row.balance_after))) }}</span>
                <span class="rp-sub">{{ balanceLabel(row.balance_after) }}</span>
              </td>
            </tr>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-cash"></i>
                  <div class="fw-semibold">No supplier payments found</div>
                  <small class="text-muted">Try another period, supplier or account type.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th colspan="6" class="text-end">Total ({{ plural(rows.length, 'payment') }})</th>
              <th class="text-end">{{ formatMoney(summary.amount) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Covers supplier due payments and journal / debit vouchers on the supplier payable. Balance after is what was
      still owed to the supplier right after the payment, across all stores.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { PAYMENT_ICONS, PAYMENT_TONES, SALES_COLOR } from '../sales/salesReportMixin';
import purchaseReport, { PURCHASE_COLOR } from './purchaseReportMixin';

const EMPTY_SUMMARY = {
  amount: 0, payment_count: 0, supplier_count: 0, avg_payment: 0, largest: null, by_account: [], by_kind: [],
  top_suppliers: [], kind_counts: { all: 0 }, trend_unit: 'day', trend: [],
};
const KIND_ICONS = { ...PAYMENT_ICONS, split: 'ti-arrows-split', other: 'ti-adjustments' };
const KIND_TONES = { ...PAYMENT_TONES, other: 'muted' };
const KIND_PILLS = [
  { key: 'cash', label: 'Cash' },
  { key: 'bank', label: 'Bank' },
  { key: 'card', label: 'Card' },
  { key: 'mobile', label: 'Mobile banking' },
  { key: 'other', label: 'Adjustment' },
];

export default {
  name: 'SupplierPaymentReport',
  mixins: [inventoryReport, periodReport, salesReport, purchaseReport],

  reportUrl: apiUrls.supplier_payments_report.get,
  fileName: 'supplier_payments',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'voucher_id', label: 'Voucher' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'account', label: 'Paid From' },
    { key: 'narration', label: 'Narration' },
    { key: 'created_by', label: 'Recorded By' },
    { key: 'amount', label: 'Amount', class: 'text-end', numeric: true },
    { key: 'balance_after', label: 'Balance After', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), supplier: null, kind: null, warehouse: null, category: null, sub_category: null,
        search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      PURCHASE_COLOR,
      SALES_COLOR,
      KIND_ICONS,
      KIND_TONES,
    };
  },

  computed: {
    hasActiveFilters() {
      const { supplier, kind, search } = this.filters;
      return this.periodChanged || !!(supplier || kind || search.trim());
    },

    kindPills() {
      const counts = this.summary.kind_counts;
      return [
        { key: 'all', label: 'All payments', count: counts.all },
        ...KIND_PILLS.map((p) => ({ ...p, icon: KIND_ICONS[p.key], count: counts[p.key] || 0 }))
          .filter((p) => p.count || this.filters.kind === p.key),
      ];
    },

    accountBars() {
      return this.withPercent(this.summary.by_account, 'amount');
    },

    mainAccount() {
      return this.summary.by_account[0] || null;
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.kind'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { kind } = this.filters;
      return { ...this.periodParams(), ...this.supplierParams(), ...(kind ? { kind } : {}) };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), supplier: null, kind: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Amount', field: 'amount', color: PURCHASE_COLOR },
        { label: 'Payments', field: 'count', color: SALES_COLOR, count: true },
      ]));
    },

    setKind(key) {
      this.filters.kind = key === 'all' ? null : key;
    },

    balanceClass(balance) {
      const n = Number(balance) || 0;
      return n > 0.005 ? 'text-danger' : n < -0.005 ? 'text-success' : 'text-muted';
    },

    balanceLabel(balance) {
      const n = Number(balance) || 0;
      return n > 0.005 ? 'still due' : n < -0.005 ? 'advance' : 'settled';
    },

    printReport() {
      const s = this.summary;
      const kind = this.kindPills.find((p) => p.key === (this.filters.kind || 'all'))?.label;
      const search = this.filters.search.trim();
      this.printColumns(
        'Supplier Payment Register',
        [this.periodText(), `Paid from: ${kind}`, this.supplierText(), this.storeText(), search && `Search: ${search}`],
        [
          ['Paid to Suppliers', `${this.formatMoney(s.amount)} · ${this.plural(s.payment_count, 'payment')}`],
          ['Suppliers Paid', `${this.formatInt(s.supplier_count)} · avg ${this.formatMoney(s.avg_payment)}`],
          ['Largest Payment', s.largest ? `${this.formatMoney(s.largest.amount)} · ${s.largest.supplier}` : '—'],
          ['By Account', s.by_account.slice(0, 2).map((a) => `${a.label} ${this.formatMoney(a.amount)}`).join(' · ') || '—'],
        ],
        [
          { label: 'Date', value: (r) => `${this.formatDate(r.date)} ${r.time}` },
          { label: 'Voucher', value: (r) => `${r.voucher_id} (${r.voucher_type})` },
          { label: 'Supplier', value: (r) => r.supplier },
          { label: 'Paid From', value: (r) => r.account },
          { label: 'Narration', value: (r) => r.narration },
          { label: 'Recorded By', value: (r) => r.created_by },
          { label: 'Amount', value: (r) => this.formatMoney(r.amount), num: true },
          { label: 'Balance After', value: (r) => this.formatMoney(r.balance_after), num: true },
        ],
        this.visibleRows,
        { 'Recorded By': 'Total', Amount: this.formatMoney(s.amount) },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
